import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Check, FileText, Wallet, ShieldCheck, X, ClipboardList } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { PHONE_DISPLAY, PHONE_TEL, ABOUT_IMAGE, TOOLBELT_IMAGE, SERVICE_BY_SLUG } from "@/lib/data";
import useSeo from "@/hooks/useSeo";

const PRICING_FAQS = [
  { q: "Do you charge for quotes?", a: "No. Every quote is free, written and itemised. For larger jobs we'll also walk the property at no cost before quoting." },
  { q: "Are your quotes fixed-price?", a: "Yes, wherever we possibly can. For straightforward work the quote is fixed before we start. Variations — only if scope changes — are quoted in writing before any extra work happens." },
  { q: "When do you ask for payment?", a: "Small jobs: on completion. Larger jobs: a deposit on acceptance, a progress payment mid-job and the balance on completion. Always documented in the quote." },
  { q: "What if I'm not happy with the work?", a: "Tell us. If it falls inside our workmanship guarantee, we come back and put it right. No fuss, no fight, no extra invoice." },
  { q: "Do you take cards / EFT / bank transfer?", a: "All of the above. Bank transfer is standard, card is fine for smaller amounts. Receipts and tax invoices issued for every payment." },
  { q: "Do you do emergency / after-hours pricing?", a: "Urgent and after-hours callouts have a small additional call-out fee that's quoted up-front before the crew is dispatched." },
];

const INCLUDED = [
  "On-site or remote scope assessment",
  "Itemised, written quote inside one business day",
  "Materials, labour and disposal — line by line",
  "Realistic timeline, not the optimistic one",
  "Clear payment milestones",
  "No-obligation: a quote is a quote, not a commitment",
];

const NOT_INCLUDED = [
  "Mystery surcharges on the final invoice",
  "Verbal-only \"trust me\" quotes",
  "Variations sprung on you mid-job",
  "Pressure-selling extras you don't need",
];

export default function Pricing() {
  useSeo({
    title: "Pricing & Quotes — RSR Australia",
    description: "Free, written, fixed-price quotes. No surprises on the invoice. Workmanship-backed. Get a quote inside one business day.",
  });
  return (
    <>
      {/* Hero with photo */}
      <section data-testid="pricing-hero" className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule-accent" />
              <span className="overline">Pricing & quotes</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] tracking-tighter leading-[0.96] font-extrabold">
              Honest quotes. <br className="hidden sm:block" /> <span className="text-accent">No surprises.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground">
              Every job gets a free, written, fixed-price quote before any work starts. What we quote is what you pay — variations only happen if scope changes, and only with your sign-off.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="pricing-hero-quote" className="group inline-flex items-center gap-3 bg-accent text-white px-7 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                Get a free quote <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="pricing-hero-call" className="inline-flex items-center gap-3 border border-foreground/25 hover:border-foreground px-7 py-4 font-semibold text-sm transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[{ k: "$0", v: "To quote your job" }, { k: "1 day", v: "Avg quote turnaround" }, { k: "0", v: "Hidden line items" }].map((s, i) => (
                <div key={i} className="border-l border-accent pl-4">
                  <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter leading-none">{s.k}</div>
                  <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -right-3 left-6 bottom-10 border border-accent/40 hidden sm:block" />
              <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
                <img src={ABOUT_IMAGE} alt="A written, itemised quote on the desk" className="w-full h-full object-cover duotone" />
                <div className="absolute inset-0 bg-gradient-to-tr from-foreground/10 to-transparent" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-foreground text-background p-5 max-w-[260px] hidden sm:block">
                <ClipboardList className="w-5 h-5 mb-2" style={{ color: "#7AB3FF" }} />
                <div className="text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Every quote</div>
                <div className="mt-1 font-display text-lg font-bold leading-tight">Itemised. Written. Plain English.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The three pillars with photos */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-border">
            {[
              { icon: FileText, t: "Written", b: "Every quote is in writing, itemised and emailed. So everyone's working off the same document.", img: SERVICE_BY_SLUG.handyman.image },
              { icon: Wallet, t: "Fixed-price", b: "We commit to the number. The invoice matches the quote — unless you ask for changes.", img: SERVICE_BY_SLUG.renovations.image },
              { icon: ShieldCheck, t: "Workmanship-backed", b: "Every job is covered by our written workmanship guarantee. If something isn't right, we come back.", img: SERVICE_BY_SLUG["general-repairs"].image },
            ].map((b, i) => (
              <div key={i} data-testid={`pricing-pillar-${i}`} className="relative bg-card border-r border-b border-border overflow-hidden flex flex-col">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={b.img} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-80" />
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground/50 via-foreground/20 to-transparent" />
                  <div className="absolute top-5 left-5 w-12 h-12 bg-accent text-white flex items-center justify-center">
                    <b.icon className="w-6 h-6" strokeWidth={1.7} />
                  </div>
                  <div className="absolute bottom-5 left-5 font-display text-3xl font-extrabold text-white drop-shadow tabular-nums">0{i + 1}</div>
                </div>
                <div className="p-8 lg:p-10 flex flex-col gap-4 flex-1">
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{b.t}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed image band */}
      <section className="relative h-[36vh] sm:h-[44vh] overflow-hidden">
        <img src={TOOLBELT_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover duotone" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/70 to-foreground/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12">
            <div className="max-w-2xl text-white">
              <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>What the invoice says</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-extrabold">
                Matches the quote. <br /> <span className="text-accent">Every time.</span>
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* Quote process */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="overline">How a quote works</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              From "tell us the job" <br /> to a number — fast.
            </h2>
            <p className="mt-6 text-base text-muted-foreground max-w-md">
              Most small and medium jobs are quoted within one business day. Larger jobs include a free on-site walkthrough before we put a number on it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold hover:bg-accent/90 transition-colors">Start a quote <ArrowUpRight className="w-4 h-4" /></Link>
            </div>
            <div className="mt-8 relative aspect-[4/3] overflow-hidden border border-border bg-secondary hidden lg:block">
              <img src={SERVICE_BY_SLUG.painting.image} alt="A tradesperson assessing work on site" className="w-full h-full object-cover duotone" />
              <div className="absolute inset-0 bg-gradient-to-tr from-foreground/30 to-transparent" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border h-fit">
            {[
              { t: "Brief us in 60 seconds", b: "Call, email or use the contact form. A photo and the suburb is plenty." },
              { t: "Quick triage same-day", b: "We assess scope, ask any clarifying questions, schedule a walkthrough if needed." },
              { t: "Written quote in 1 day", b: "Itemised. Materials, labour, timeline, payment milestones. Plain English." },
              { t: "You decide", b: "No pressure, no follow-up spam. Accept when ready and we'll book the crew." },
            ].map((s, i) => (
              <div key={i} data-testid={`pricing-step-${i}`} className="bg-background p-6 flex flex-col gap-3 min-h-[180px]">
                <div className="font-display text-2xl font-extrabold text-accent leading-none tabular-nums">0{i + 1}</div>
                <h3 className="font-display text-lg font-bold tracking-tight">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Included / Not included with visual cards */}
      <section className="py-20 sm:py-24 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline">What's in every quote</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">No fine print. <br /> No "while we're there".</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px border border-border bg-border">
            <div className="relative bg-background p-8 lg:p-10 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full -mr-16 -mt-16" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-accent text-white flex items-center justify-center"><Check className="w-5 h-5" strokeWidth={2.5} /></div>
                  <h3 className="font-display text-2xl font-bold tracking-tight">Always included</h3>
                </div>
                <ul className="space-y-3.5">
                  {INCLUDED.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-accent flex-shrink-0 mt-1" strokeWidth={2.5} />
                      <span className="text-foreground/90">{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="relative bg-card p-8 lg:p-10 overflow-hidden">
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-foreground/10 text-foreground/60 flex items-center justify-center"><X className="w-5 h-5" strokeWidth={2.5} /></div>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-foreground/80">Never on the invoice</h3>
                </div>
                <ul className="space-y-3.5">
                  {NOT_INCLUDED.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <X className="w-4 h-4 text-foreground/40 flex-shrink-0 mt-1" strokeWidth={2.5} />
                      <span className="text-foreground/70 line-through decoration-foreground/30">{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Pricing FAQ</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">Money questions.</h2>
            <p className="mt-6 text-sm text-muted-foreground">Can't see the answer you need? Call us — we'll explain it on the phone.</p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {PRICING_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-display text-lg sm:text-xl font-semibold tracking-tight hover:no-underline hover:text-accent py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6 max-w-2xl">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden bg-foreground text-background">
            <img src={ABOUT_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover duotone opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/40" />
            <div className="relative p-8 lg:p-14 grid grid-cols-12 gap-8 items-center">
              <div className="col-span-12 lg:col-span-8">
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Ready to start</p>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-extrabold leading-[1.05]">
                  Get a real number for your job.
                </h3>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
                <Link to="/contact" className="inline-flex items-center justify-between gap-3 bg-accent text-white px-6 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                  Get a free quote <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center justify-between gap-3 border border-white/30 hover:border-white px-6 py-4 font-semibold text-sm text-white transition-colors">
                  <span className="inline-flex items-center gap-2"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</span>
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
